import React, { useState, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, Download, Maximize2, Minimize2, Presentation, Clock, Award } from 'lucide-react';
import { usePreApproval } from '../../pages/LandingPage';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PresentationViewer = () => {
  const { openPreApproval } = usePreApproval();
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [containerWidth, setContainerWidth] = useState(800);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const pdfUrl = `${API_URL}/api/presentation/pdf`;
  const downloadUrl = 'https://customer-assets.emergentagent.com/job_oneforce-wizard/artifacts/7j8ftcw7_AI_Horse-Powered_Growth_Platform%20.pdf';

  // Check for mobile and set container width
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setContainerWidth(mobile ? window.innerWidth - 32 : Math.min(window.innerWidth - 100, 900));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
  }, []);

  const goToPrevPage = () => {
    if (pageNumber > 1) setPageNumber(prev => prev - 1);
  };

  const goToNextPage = () => {
    if (pageNumber < (numPages || 1)) setPageNumber(prev => prev + 1);
  };

  const goToPage = (page) => {
    if (page !== pageNumber) setPageNumber(page);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrevPage();
      if (e.key === 'ArrowRight') goToNextPage();
      if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pageNumber, numPages, isFullscreen]);

  // Handle fullscreen - only for desktop
  const toggleFullscreen = () => {
    if (isMobile) return; // Disabled on mobile
    setIsFullscreen(!isFullscreen);
  };

  // Lock body scroll in fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isFullscreen]);

  const isCtaSlide = numPages && (pageNumber >= numPages - 1);

  // Calculate page dimensions
  const getPageWidth = () => {
    if (isFullscreen) {
      return Math.min(window.innerWidth - 80, 1100);
    }
    return containerWidth;
  };

  // Fullscreen overlay
  if (isFullscreen) {
    return (
      <div 
        className="fixed inset-0 bg-slate-950 flex flex-col"
        style={{ zIndex: 999999 }}
      >
        {/* Header */}
        <div className="bg-slate-800 border-b border-slate-700 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-slate-300 text-sm font-medium">AI Horse-Powered Growth Platform</span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-emerald-400 text-sm flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download
            </a>
            <button
              onClick={() => setIsFullscreen(false)}
              className="text-slate-400 hover:text-white text-sm flex items-center gap-2"
            >
              <Minimize2 className="w-4 h-4" />
              Exit
            </button>
          </div>
        </div>

        {/* PDF Content */}
        <div className="flex-1 flex items-center justify-center overflow-auto bg-slate-800 p-4">
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            className="flex justify-center"
            loading={
              <div className="flex items-center justify-center h-96 bg-slate-800">
                <div className="w-8 h-8 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              width={getPageWidth()}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="shadow-2xl"
              loading={
                <div 
                  className="flex items-center justify-center bg-slate-800" 
                  style={{ width: getPageWidth(), height: 500 }}
                >
                  <div className="w-6 h-6 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
                </div>
              }
            />
          </Document>
          
          {isCtaSlide && (
            <button
              onClick={() => { setIsFullscreen(false); openPreApproval(); }}
              className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-6 py-3 rounded-xl shadow-lg"
            >
              Get Started Now →
            </button>
          )}
        </div>

        {/* Navigation */}
        <div className="bg-slate-800 border-t border-slate-700 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <button
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${pageNumber <= 1 ? 'text-slate-600' : 'text-slate-300 hover:bg-slate-700'}`}
          >
            <ChevronLeft className="w-5 h-5" /> Previous
          </button>
          
          <div className="flex items-center gap-2">
            {numPages && Array.from({ length: numPages }, (_, i) => (
              <button
                key={i}
                onClick={() => goToPage(i + 1)}
                className={`w-2.5 h-2.5 rounded-full ${pageNumber === i + 1 ? 'bg-emerald-400 w-5' : 'bg-slate-600 hover:bg-slate-500'}`}
              />
            ))}
            <span className="text-slate-400 text-sm ml-3">{pageNumber} / {numPages || '...'}</span>
          </div>
          
          <button
            onClick={goToNextPage}
            disabled={pageNumber >= (numPages || 1)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${pageNumber >= (numPages || 1) ? 'text-slate-600' : 'text-slate-300 hover:bg-slate-700'}`}
          >
            Next <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        
        <div className="text-center py-2 text-slate-500 text-xs bg-slate-800">
          Press ESC to exit
        </div>
      </div>
    );
  }

  // Normal view
  return (
    <section className="py-8 md:py-12 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-4 py-1.5 mb-3">
            <Presentation className="w-4 h-4 text-emerald-400" />
            <span className="text-white font-semibold text-sm">Interactive Presentation</span>
          </div>
          
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">
            Discover the <span className="text-emerald-400">AI Horse-Powered</span> Growth Platform
          </h2>
          
          <p className="text-sm md:text-base text-slate-400 max-w-2xl mx-auto mb-3">
            Explore how our platform transforms businesses with $120,000 in Co-Op Funds.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 text-xs">
            <div className="flex items-center gap-2 text-slate-400">
              <Clock className="w-3 h-3 text-cyan-400" />
              <span>8 min read</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <ChevronRight className="w-3 h-3 text-emerald-400" />
              <span>Use arrows to navigate</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <Award className="w-3 h-3 text-amber-400" />
              <span>See what you qualify for</span>
            </div>
          </div>
        </div>

        {/* Presentation Container */}
        <div className="relative max-w-4xl mx-auto">
          <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/20 via-cyan-500/20 to-emerald-500/20 rounded-2xl blur-sm hidden md:block" />
          
          <div className="relative bg-slate-900 border border-slate-700/50 rounded-xl md:rounded-2xl overflow-hidden shadow-xl">
            {/* Header Bar */}
            <div className="bg-slate-800 border-b border-slate-700/50 px-3 md:px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="flex gap-1 md:gap-1.5">
                  <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-red-500/80" />
                  <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-yellow-500/80" />
                  <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-green-500/80" />
                </div>
                <span className="text-slate-400 text-xs hidden sm:inline">AI Horse-Powered</span>
              </div>
              
              <div className="flex items-center gap-2 md:gap-3">
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-emerald-400 text-xs flex items-center gap-1"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">PDF</span>
                </a>
                
                {/* Only show fullscreen button on desktop */}
                {!isMobile && (
                  <button
                    onClick={toggleFullscreen}
                    className="text-slate-400 hover:text-cyan-400 text-xs flex items-center gap-1"
                  >
                    <Maximize2 className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Fullscreen</span>
                  </button>
                )}
              </div>
            </div>

            {/* PDF Viewer */}
            <div className="bg-slate-800 flex justify-center py-2 md:py-3 overflow-hidden min-h-[200px] md:min-h-[400px]">
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                className="flex justify-center"
                loading={
                  <div className="flex items-center justify-center" style={{ height: isMobile ? 250 : 420 }}>
                    <div className="w-8 h-8 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
                  </div>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  width={getPageWidth()}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="shadow-lg"
                  loading={
                    <div 
                      className="flex items-center justify-center bg-slate-800" 
                      style={{ width: getPageWidth(), height: isMobile ? 250 : 420 }}
                    >
                      <div className="w-6 h-6 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
                    </div>
                  }
                />
              </Document>
            </div>

            {/* CTA for last slides */}
            {isCtaSlide && (
              <div className="bg-slate-800/50 py-3 flex justify-center">
                <button
                  onClick={openPreApproval}
                  className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-5 py-2 rounded-lg text-sm"
                >
                  Get Started Now →
                </button>
              </div>
            )}

            {/* Navigation */}
            <div className="bg-slate-800 border-t border-slate-700/50 px-2 md:px-4 py-2 md:py-3 flex items-center justify-between">
              <button
                onClick={goToPrevPage}
                disabled={pageNumber <= 1}
                className={`px-2 md:px-4 py-1.5 md:py-2 rounded-lg flex items-center gap-1 text-sm ${
                  pageNumber <= 1 ? 'text-slate-600' : 'text-slate-300 hover:bg-slate-700/50'
                }`}
              >
                <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
                <span className="hidden sm:inline">Prev</span>
              </button>

              <div className="flex items-center gap-1 md:gap-2">
                {/* Show dots only on larger screens */}
                <div className="hidden md:flex items-center gap-1.5">
                  {numPages && Array.from({ length: numPages }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => goToPage(i + 1)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        pageNumber === i + 1 ? 'bg-emerald-400 w-4' : 'bg-slate-600 hover:bg-slate-500'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-slate-400 text-xs md:text-sm md:ml-2">
                  {pageNumber} / {numPages || '...'}
                </span>
              </div>

              <button
                onClick={goToNextPage}
                disabled={pageNumber >= (numPages || 1)}
                className={`px-2 md:px-4 py-1.5 md:py-2 rounded-lg flex items-center gap-1 text-sm ${
                  pageNumber >= (numPages || 1) ? 'text-slate-600' : 'text-slate-300 hover:bg-slate-700/50'
                }`}
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-4 text-center">
          <p className="text-emerald-400 font-medium text-sm">
            Scroll down to begin your qualification journey
          </p>
        </div>
      </div>
    </section>
  );
};

export default PresentationViewer;
